import { Ionicons } from '@expo/vector-icons';
import { type Href, router } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { ActivityIndicator, Button, Modal, Text } from '@/components/ui';
import { apiService } from '@/services/api-service';

interface DebugQuery {
	idx: number;
	sql: string;
	paramCount: number;
	inlined: string;
	terminal: string;
	durationMs: number;
	rowCount: number | null;
}

export interface SqlDebugModalProps {
	visible: boolean;
	onDismiss: () => void;
	apiPath: string;
	reportTitle: string;
}

const MONO = Platform.select({ web: 'ui-monospace, Menlo, monospace', default: 'Courier' });

const buildDebugUrl = (apiPath: string): string => {
	const sep = apiPath.includes('?') ? '&' : '?';
	return `${apiPath}${sep}debug=sql`;
};

const toBase64Url = (s: string): string => {
	if (typeof btoa === 'function') {
		const b64 = btoa(unescape(encodeURIComponent(s)));
		return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
	}
	return Buffer.from(s, 'utf-8').toString('base64')
		.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const copyToClipboard = async (text: string): Promise<boolean> => {
	if (Platform.OS !== 'web') return false;
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
};

export const SqlDebugModal: React.FC<SqlDebugModalProps> = ({
	visible, onDismiss, apiPath, reportTitle,
}) => {
	const [queries, setQueries] = useState<DebugQuery[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

	const load = React.useCallback(async () => {
		setLoading(true);
		setError(null);
		setQueries(null);
		try {
			const res: any = await apiService.fetchApi(buildDebugUrl(apiPath));
			const dbg = res?.__debug?.queries;
			if (!Array.isArray(dbg)) {
				setError('Endpoint did not return __debug.queries — is `sql_admin` enabled, and does the endpoint support ?debug=sql?');
				return;
			}
			setQueries(dbg);
		} catch (e: any) {
			setError(e?.message ?? String(e));
		} finally {
			setLoading(false);
		}
	}, [apiPath]);

	React.useEffect(() => {
		if (visible) load();
		else { setQueries(null); setError(null); setCopiedIdx(null); }
	}, [visible, load]);

	const openInSqlAdmin = (q: DebugQuery) => {
		const sqlText = q.inlined;
		const title = `${reportTitle} · q${q.idx}`;
		router.push(`/admin/sql?sql=${toBase64Url(sqlText)}&title=${encodeURIComponent(title)}` as Href);
	};

	const openAllInSqlAdmin = () => {
		if (!queries || queries.length === 0) return;
		const bundled = queries
			.map((q) => {
				const trimmed = q.inlined.trim().replace(/;+\s*$/, '');
				return `-- q${q.idx} (${q.terminal}, ${q.durationMs}ms${q.rowCount != null ? `, ${q.rowCount} row${q.rowCount === 1 ? '' : 's'}` : ''})\n${trimmed};`;
			})
			.join('\n\n');
		const title = queries.length === 1 ? reportTitle : `${reportTitle} · ${queries.length} queries`;
		router.push(`/admin/sql?sql=${toBase64Url(bundled)}&title=${encodeURIComponent(title)}` as Href);
	};

	const onCopy = async (q: DebugQuery) => {
		const ok = await copyToClipboard(q.inlined);
		if (ok) {
			setCopiedIdx(q.idx);
			setTimeout(() => setCopiedIdx((cur) => (cur === q.idx ? null : cur)), 1500);
		}
	};

	const totalDuration = useMemo(
		() => (queries ?? []).reduce((s, q) => s + (q.durationMs || 0), 0),
		[queries],
	);

	return (
		<Modal
			visible={visible}
			onDismiss={onDismiss}
			contentContainerStyle={{ width: '95%', maxWidth: 1100 }}
		>
			<View style={styles.header}>
				<View style={{ flex: 1 }}>
					<Text style={styles.title}>Debug SQL · {reportTitle}</Text>
					<Text style={styles.subtitle}>
						Captured the SQL the report endpoint just ran. Each query shows `?` substituted inline so you can paste it into the SQL Admin editor.
					</Text>
				</View>
				{queries && queries.length > 0 && (
					<Button mode="contained" compact onPress={openAllInSqlAdmin}>
						{queries.length > 1 ? `Open all ${queries.length} in SQL Admin` : 'Open in SQL Admin'}
					</Button>
				)}
				<Pressable onPress={onDismiss} style={styles.closeBtn} accessibilityLabel="Close">
					<Ionicons name="close" size={20} color="#666" />
				</Pressable>
			</View>

			<ScrollView style={{ maxHeight: 560 }} contentContainerStyle={{ gap: 12, paddingBottom: 8 }}>
				{loading && (
					<View style={{ padding: 24, alignItems: 'center' }}>
						<ActivityIndicator />
						<Text style={{ marginTop: 8, color: '#666' }}>Capturing SQL…</Text>
					</View>
				)}
				{error && (
					<View style={styles.errorBox}>
						<Text style={{ color: '#c62828', fontWeight: '600', marginBottom: 4 }}>Failed to load debug SQL</Text>
						<Text style={{ fontFamily: MONO, fontSize: 12 }}>{error}</Text>
					</View>
				)}
				{queries && queries.length === 0 && (
					<Text style={{ color: '#666', padding: 12 }}>No queries were captured.</Text>
				)}
				{queries && queries.map((q) => (
					<View key={q.idx} style={styles.queryCard}>
						<View style={styles.queryHeader}>
							<Text style={styles.queryIdx}>q{q.idx}</Text>
							<Text style={styles.queryMeta}>
								{q.terminal} · {q.durationMs}ms
								{q.rowCount != null ? ` · ${q.rowCount} row${q.rowCount === 1 ? '' : 's'}` : ''}
								{q.paramCount ? ` · ${q.paramCount} param${q.paramCount === 1 ? '' : 's'}` : ''}
							</Text>
							<View style={{ flexDirection: 'row', gap: 6 }}>
								<Button mode="outlined" compact onPress={() => onCopy(q)}>
									{copiedIdx === q.idx ? 'Copied' : 'Copy'}
								</Button>
								<Button mode="contained" compact onPress={() => openInSqlAdmin(q)}>
									Open in SQL Admin
								</Button>
							</View>
						</View>
						<Text selectable style={styles.sqlBody}>{q.inlined}</Text>
					</View>
				))}
			</ScrollView>

			{queries && queries.length > 0 && (
				<Text style={styles.footer}>
					{queries.length} quer{queries.length === 1 ? 'y' : 'ies'} · {totalDuration}ms total
				</Text>
			)}
		</Modal>
	);
};

const styles = StyleSheet.create({
	header: {
		flexDirection: 'row', alignItems: 'flex-start', gap: 12,
		paddingBottom: 10, marginBottom: 10,
		borderBottomWidth: 1, borderBottomColor: '#eee',
	},
	title: { fontSize: 16, fontWeight: '700', color: '#212121' },
	subtitle: { fontSize: 12, color: '#666', marginTop: 4 },
	closeBtn: { padding: 4 },
	errorBox: {
		borderWidth: 1, borderColor: '#f1aaaa', borderRadius: 6,
		padding: 12, backgroundColor: '#fff5f5',
	},
	queryCard: {
		borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 6,
		backgroundColor: '#fafafa',
	},
	queryHeader: {
		flexDirection: 'row', alignItems: 'center', gap: 10,
		padding: 8, borderBottomWidth: 1, borderBottomColor: '#eee',
		backgroundColor: '#f0f0f0',
		borderTopLeftRadius: 6, borderTopRightRadius: 6,
	},
	queryIdx: { fontFamily: MONO, fontWeight: '700', fontSize: 12, color: '#1976d2', minWidth: 30 },
	queryMeta: { flex: 1, fontSize: 11, color: '#666' },
	sqlBody: {
		fontFamily: MONO, fontSize: 12, color: '#212121',
		padding: 10, lineHeight: 18,
	},
	footer: { textAlign: 'center', color: '#90a4ae', fontSize: 11, marginTop: 8 },
});
